/* Stone Compare engine — powers the PDP "Compare Similar Diamonds/Gemstones"
   tables (sections/diamond-compare.liquid + gemstone-compare.liquid).

   Candidates come from a filtered collection fetch of
   sections/stone-compare-data.liquid via the Section Rendering API, inside a
   price window around the anchor stone. Tiers: [0.70x, 1.40x] ->
   [0.50x, 2.0x] -> [0.35x, 3.0x]; the 3x ceiling is a hard cap so a $741
   stone can never be "compared" to a five-figure one.

   One toggle switches between same-shape stones (default) and other shapes;
   gemstones NEVER cross gemstone type in either mode, and color is a hard
   constraint everywhere (blue sapphires only see blue sapphires, white
   diamonds only see white diamonds). The toggle renders only when the other
   view actually has stones. Target is 4 columns, widening the price tiers
   to get there; fewer render only when inventory truly runs out.

   Selection is deterministic and skips near-duplicate stones (same color,
   clarity, cut, ~same carat) so every column earns its place, and labels are
   factual grade language only. "Better Value" is banned. */
(function () {
  'use strict';

  /* ---- rank tables (grade ladders) ---- */
  var CUT_RANKS = [
    ['super ideal', 7], ['ideal', 6], ['excellent', 5], ['very good', 4],
    ['good', 3], ['fair', 2], ['poor', 1]
  ];
  function cutRank(v) {
    v = String(v || '').toLowerCase();
    for (var i = 0; i < CUT_RANKS.length; i++) {
      if (v.indexOf(CUT_RANKS[i][0]) !== -1) return CUT_RANKS[i][1];
    }
    return 0;
  }
  function colorRankDiamond(v) {
    v = String(v || '').toUpperCase().trim();
    if (v.length !== 1) return 0; /* fancy colors, ranges like "S-T" -> 0, never claimed */
    var i = 'DEFGHIJKLMNOPQRSTUVWXYZ'.indexOf(v);
    return i === -1 ? 0 : 23 - i;
  }
  var CLARITY_DIAMOND = { FL: 11, IF: 10, VVS1: 9, VVS2: 8, VS1: 7, VS2: 6, SI1: 5, SI2: 4, I1: 3, I2: 2, I3: 1 };
  var CLARITY_GEM = {
    'FL': 11, 'LOUPE CLEAN': 10, 'IF': 10, 'VVS1': 9, 'VVS2': 8, 'VS1': 7,
    'EYE CLEAN': 7, 'VS2': 6, 'SLIGHTLY INCLUDED': 5, 'SI1': 5, 'SI2': 4,
    'INCLUDED': 3, 'I1': 3, 'I2': 2, 'I3': 1
  };
  function clarityRank(v, isGem) {
    v = String(v || '').toUpperCase().trim();
    return (isGem ? CLARITY_GEM : CLARITY_DIAMOND)[v] || 0;
  }

  var TIERS = [[0.70, 1.40], [0.50, 2.0], [0.35, 3.0]];
  var moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  function money(cents) { return moneyFmt.format(cents / 100); }

  function initWrapper(w) {
    var mode = w.getAttribute('data-mode'); /* 'diamond' | 'gemstone' */
    var isGem = mode === 'gemstone';
    var collectionUrl = w.getAttribute('data-collection-url');
    var dataSection = w.getAttribute('data-data-section') || 'stone-compare-data';
    var shapeParam = w.getAttribute('data-shape-param');
    var typeParam = w.getAttribute('data-type-param');
    var maxCols = parseInt(w.getAttribute('data-max'), 10) || 4;
    var noun = w.getAttribute('data-noun') || 'Diamond';

    var anchor = {
      id: w.getAttribute('data-anchor-id'),
      price: parseInt(w.getAttribute('data-anchor-price'), 10),
      carat: parseFloat(w.getAttribute('data-anchor-carat')) || 0,
      color: w.getAttribute('data-anchor-color') || '',
      clarity: w.getAttribute('data-anchor-clarity') || '',
      cut: w.getAttribute('data-anchor-cut') || '',
      shape: w.getAttribute('data-anchor-shape') || '',
      type: w.getAttribute('data-anchor-type') || ''
    };
    if (!anchor.price || anchor.price <= 0) return; /* nothing sane to window on */
    anchor.colorRank = isGem ? 0 : colorRankDiamond(anchor.color);
    anchor.clarityRank = clarityRank(anchor.clarity, isGem);
    anchor.cutRank = isGem ? 0 : cutRank(anchor.cut);

    /* HARD color compatibility: a blue sapphire never sees a pink sapphire,
       a white diamond never sees a black/fancy one, and vice versa.
       - Gemstones: exact color match (Blue -> Blue only).
       - Diamonds: white (D-Z) anchors -> white candidates only; fancy/black
         anchors -> exact same color value only.
       - Anchors with no color on file skip the constraint (nothing to match). */
    function normColor(v) { return String(v || '').toUpperCase().trim(); }
    var anchorColorNorm = normColor(anchor.color);
    function colorCompatible(c) {
      if (!anchorColorNorm) return true;
      if (isGem) return normColor(c.color) === anchorColorNorm;
      if (anchor.colorRank > 0) return c.colorRank > 0;
      return normColor(c.color) === anchorColorNorm;
    }

    var status = w.querySelector('.scmp__status');
    var toggleWrap = w.querySelector('.scmp__chips');
    var toggleBtns = w.querySelectorAll('[data-scmp-view]');
    var cache = {};        /* fetch url -> parsed candidate list */
    var gemPool = null;    /* gemstone mode: full same-type pool, filtered in memory */
    var viewMode = 'same'; /* 'same' | 'other' */
    var revealed = false;

    /* ---------------- fetching ---------------- */

    function tierUrl(tier, shape) {
      var lo = Math.max(0, Math.floor((anchor.price / 100) * tier[0]));
      var hi = Math.ceil((anchor.price / 100) * tier[1]);
      var url = collectionUrl + '?section_id=' + encodeURIComponent(dataSection) +
        '&sort_by=price-ascending' +
        '&filter.v.price.gte=' + lo +
        '&filter.v.price.lte=' + hi;
      if (!isGem && shape && shapeParam) url += '&' + shapeParam + '=' + encodeURIComponent(shape);
      if (isGem && anchor.type && typeParam) url += '&' + typeParam + '=' + encodeURIComponent(anchor.type);
      return url;
    }

    function parseCandidates(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var seen = {};
      var out = [];
      doc.querySelectorAll('.scmp-cand').forEach(function (el) {
        var id = el.getAttribute('data-id');
        if (!id || seen[id] || id === anchor.id) return;
        seen[id] = true;
        var c = {
          id: id,
          url: el.getAttribute('data-url'),
          title: el.getAttribute('data-title') || '',
          price: parseInt(el.getAttribute('data-price'), 10) || 0,
          carat: parseFloat(el.getAttribute('data-carat')) || 0,
          color: el.getAttribute('data-color') || '',
          clarity: el.getAttribute('data-clarity') || '',
          cut: el.getAttribute('data-cut') || '',
          shape: el.getAttribute('data-shape') || '',
          type: el.getAttribute('data-type') || '',
          imgHTML: el.innerHTML
        };
        c.colorRank = isGem ? 0 : colorRankDiamond(c.color);
        c.clarityRank = clarityRank(c.clarity, isGem);
        c.cutRank = isGem ? 0 : cutRank(c.cut);
        out.push(c);
      });
      return out;
    }

    function fetchUrl(url) {
      if (cache[url]) return Promise.resolve(cache[url]);
      return fetch(url).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      }).then(function (html) {
        var list = parseCandidates(html);
        cache[url] = list;
        return list;
      });
    }

    /* Widen tier by tier until the pool yields `maxCols` DISTINCT columns
       (post-dedupe), or the tiers run out. Each tier's window is a superset
       of the previous, so the widest successful fetch IS the whole pool. */
    function gather(shape, post) {
      var idx = 0;
      function apply(list) { return post ? list.filter(post) : list; }
      function step(prev) {
        if (idx >= TIERS.length) return Promise.resolve(prev);
        var url = tierUrl(TIERS[idx], shape);
        idx++;
        return fetchUrl(url).then(function (list) {
          var kept = apply(list);
          if (selectCandidates(kept, false).length >= maxCols) return kept;
          return step(kept);
        });
      }
      return step([]);
    }

    /* ---------------- selection ---------------- */

    function byPriceDistance(a, b) {
      var d = Math.abs(a.price - anchor.price) - Math.abs(b.price - anchor.price);
      return d !== 0 ? d : (a.id < b.id ? -1 : 1);
    }

    /* Near-duplicate key: same shape + grades + carat to the nearest 0.05ct
       reads as the same stone twice — columns must each earn their place. */
    function profileKey(c) {
      return [c.shape, c.colorRank, c.clarityRank, c.cutRank, Math.round(c.carat * 20)].join('|');
    }

    function selectCandidates(list, diverseShapes) {
      var sorted = list.slice().sort(byPriceDistance);
      var picked = [];
      var profiles = {};
      var labelsUsed = {};
      var shapesUsed = {};
      function take(c) {
        picked.push(c);
        profiles[profileKey(c)] = true;
        labelsUsed[labelFor(c)] = true;
        shapesUsed[c.shape] = true;
      }
      /* HARD RULE: every column must tell a different story. A candidate
         whose label or stone profile duplicates a picked column is skipped,
         even if that means fewer columns. */
      function blocked(c) {
        return picked.indexOf(c) !== -1 || profiles[profileKey(c)] || labelsUsed[labelFor(c)];
      }
      function pickOne(pred) {
        if (picked.length >= maxCols) return;
        for (var i = 0; i < sorted.length; i++) {
          var c = sorted[i];
          if (blocked(c)) continue;
          if (pred(c)) { take(c); return; }
        }
      }
      function fill(uniqueShape) {
        for (var i = 0; i < sorted.length && picked.length < maxCols; i++) {
          var c = sorted[i];
          if (blocked(c)) continue;
          if (uniqueShape && shapesUsed[c.shape]) continue;
          take(c);
        }
      }
      /* diversity: one larger, one higher grade, one lower-priced */
      pickOne(function (c) { return anchor.carat > 0 && c.carat > anchor.carat; });
      if (isGem) {
        pickOne(function (c) { return anchor.clarityRank > 0 && c.clarityRank > anchor.clarityRank; });
      } else {
        pickOne(function (c) { return anchor.colorRank > 0 && c.colorRank > anchor.colorRank; });
      }
      pickOne(function (c) { return c.price < anchor.price; });
      /* fill: distinct shapes first in the other-shapes view, then any
         still-distinct stone */
      if (diverseShapes) fill(true);
      fill(false);
      picked.sort(function (a, b) { return a.price - b.price; });
      return picked;
    }

    /* ---------------- labels (grade language only; no "Better Value") ---------------- */

    function labelFor(c) {
      var betters = [];
      var worses = [];
      function claim(mine, theirs, up, down) {
        if (mine > 0 && theirs > 0) {
          if (theirs > mine) betters.push(up);
          else if (theirs < mine) worses.push(down);
        }
      }
      claim(anchor.carat, c.carat, 'Larger Carat', 'Smaller Carat');
      if (!isGem) claim(anchor.colorRank, c.colorRank, 'Higher Color Grade', 'Lower Color Grade');
      claim(anchor.clarityRank, c.clarityRank, 'Higher Clarity Grade', 'Lower Clarity Grade');
      if (!isGem) claim(anchor.cutRank, c.cutRank, 'Higher Cut Grade', 'Lower Cut Grade');

      var parts = betters.slice(0, 2);
      if (worses.length) parts.push(worses[0]);
      if (!parts.length && worses.length) parts = worses.slice(0, 2);
      if (parts.length) return parts.join(', ');
      if (c.price < anchor.price) return 'Lower Price';
      if (c.price > anchor.price) return 'Higher Price';
      if (isGem && anchor.type) return 'Similar ' + anchor.type;
      return 'Similar ' + noun;
    }

    /* ---------------- rendering ---------------- */

    function arrowSpan(cls, arrow, text) {
      var s = document.createElement('span');
      s.className = cls;
      s.innerHTML = arrow + ' ';
      s.appendChild(document.createTextNode(text));
      return s;
    }

    function rankedCell(td, value, mine, theirs) {
      var v = value || '—';
      if (theirs > mine && mine > 0 && theirs > 0) td.appendChild(arrowSpan('scmp__up', '&uarr;', v));
      else if (theirs < mine && mine > 0 && theirs > 0) td.appendChild(arrowSpan('scmp__down', '&darr;', v));
      else td.textContent = v;
    }

    function buildCell(rowKey, c) {
      var td = document.createElement('td');
      td.className = 'scmp__card-td' + (rowKey === 'image' ? ' scmp__img-td' : '');
      td.setAttribute('data-href', c.url);
      td.setAttribute('tabindex', '0');
      td.setAttribute('role', 'link');
      td.setAttribute('aria-label', 'View ' + c.title);
      switch (rowKey) {
        case 'image':
          td.innerHTML = c.imgHTML;
          break;
        case 'label': {
          var span = document.createElement('span');
          span.className = 'scmp__label';
          span.textContent = labelFor(c);
          td.appendChild(span);
          break;
        }
        case 'price': {
          var p = document.createElement('span');
          p.className = 'scmp__price-val';
          p.textContent = money(c.price);
          td.appendChild(p);
          break;
        }
        case 'diff': {
          var diff = c.price - anchor.price;
          if (diff > 0) td.appendChild(arrowSpan('scmp__down', '&uarr;', money(diff)));
          else if (diff < 0) td.appendChild(arrowSpan('scmp__up', '&darr;', money(-diff)));
          else td.innerHTML = '&mdash;';
          break;
        }
        case 'shape':
          td.textContent = c.shape || '—';
          break;
        case 'carat':
          if (anchor.carat > 0 && c.carat > 0) rankedCell(td, String(c.carat), anchor.carat, c.carat);
          else td.textContent = c.carat > 0 ? String(c.carat) : '—';
          break;
        case 'cut':
          rankedCell(td, c.cut, anchor.cutRank, c.cutRank);
          break;
        case 'color':
          if (isGem) td.textContent = c.color || '—'; /* descriptive, never ranked */
          else rankedCell(td, String(c.color || '').toUpperCase(), anchor.colorRank, c.colorRank);
          break;
        case 'clarity':
          rankedCell(td, String(c.clarity || '').toUpperCase(), anchor.clarityRank, c.clarityRank);
          break;
        case 'cta': {
          var a = document.createElement('a');
          a.className = 'scmp__view-link';
          a.href = c.url;
          a.textContent = 'View ' + noun;
          td.appendChild(a);
          break;
        }
      }
      return td;
    }

    function clearCandidateCells() {
      w.querySelectorAll('tr[data-row] td.scmp__card-td').forEach(function (td) { td.remove(); });
    }

    function render(picked) {
      clearCandidateCells();
      hideStatus();
      w.querySelectorAll('tr[data-row]').forEach(function (tr) {
        var key = tr.getAttribute('data-row');
        picked.forEach(function (c) { tr.appendChild(buildCell(key, c)); });
      });
      if (!revealed) { w.hidden = false; revealed = true; }
      initSpins();
    }

    function showStatus(html) {
      if (!status) return;
      status.innerHTML = html;
      status.hidden = false;
    }
    function hideStatus() {
      if (status) { status.hidden = true; status.innerHTML = ''; }
    }

    /* ---------------- same-shape / other-shapes toggle ---------------- */

    function syncToggle() {
      for (var i = 0; i < toggleBtns.length; i++) {
        toggleBtns[i].setAttribute(
          'aria-pressed',
          toggleBtns[i].getAttribute('data-scmp-view') === viewMode ? 'true' : 'false'
        );
      }
    }

    function showToggle(show) {
      if (toggleWrap) toggleWrap.hidden = !show;
      syncToggle();
    }

    function isOtherShape(c) { return c.shape !== anchor.shape; }
    function isSameShape(c) { return c.shape === anchor.shape; }
    function sameShapeCompatible(c) { return isSameShape(c) && colorCompatible(c); }
    function otherShapeCompatible(c) { return isOtherShape(c) && colorCompatible(c); }

    function candidatesFor(target) {
      if (isGem) {
        var pool = gemPool || [];
        return Promise.resolve(pool.filter(target === 'same' ? isSameShape : isOtherShape));
      }
      return target === 'same'
        ? gather(anchor.shape, colorCompatible)
        : gather(null, otherShapeCompatible);
    }

    function switchTo(target) {
      candidatesFor(target).then(function (list) {
        viewMode = target;
        syncToggle();
        if (!list.length) {
          clearCandidateCells();
          showStatus(target === 'other'
            ? '<p>No other shapes in a similar price range right now. <a href="' + collectionUrl + '">Browse the full collection</a></p>'
            : '<p>No similar ' + anchor.shape.toLowerCase() + ' stones in this price range right now. <a href="' + collectionUrl + '">Browse the full collection</a></p>');
          return;
        }
        render(selectCandidates(list, target === 'other'));
      }).catch(function (err) {
        console.warn('[stone-compare] toggle fetch failed:', err);
        clearCandidateCells();
        showStatus('<p>Couldn’t load comparisons. <button type="button" class="scmp__retry" data-target="' + target + '">Retry</button></p>');
      });
    }

    /* ---------------- interactions ---------------- */

    w.addEventListener('click', function (e) {
      var retry = e.target.closest('.scmp__retry');
      if (retry) { switchTo(retry.getAttribute('data-target')); return; }
      var seg = e.target.closest('[data-scmp-view]');
      if (seg) {
        var target = seg.getAttribute('data-scmp-view');
        if (target !== viewMode) switchTo(target);
        return;
      }
      var cell = e.target.closest('[data-href]');
      if (!cell || e.target.closest('a')) return;
      window.location.href = cell.getAttribute('data-href');
    });
    w.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var cell = e.target.closest('[data-href]');
      if (!cell) return;
      e.preventDefault();
      window.location.href = cell.getAttribute('data-href');
    });
    var scroller = w.querySelector('[data-scmp-scroll]');
    if (scroller) {
      var checkEnd = function () {
        scroller.toggleAttribute('data-scrolled-end', scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2);
      };
      scroller.addEventListener('scroll', checkEnd, { passive: true });
      window.addEventListener('resize', checkEnd, { passive: true });
      checkEnd();
    }

    function initSpins() {
      var wraps = w.querySelectorAll('.scmp__img-wrap[data-spin]:not([data-spin-init])');
      if (!wraps.length) return;
      wraps.forEach(function (m) { m.setAttribute('data-spin-init', '1'); });

      try {
        var firstSrc = wraps[0].dataset.spin;
        var domain = new URL(firstSrc).origin;
        var link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = domain;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      } catch (e) {}

      var isMobile = window.matchMedia && window.matchMedia('(hover: none)').matches;

      wraps.forEach(function (media) {
        var iframe = null;
        var loaded = false;
        var unreachable = false;
        var loadTimer = null;

        var spinUrl = media.dataset.spin;
        var isVideo = /\.(mp4|mov|webm|ogg)(\?|$)/i.test(spinUrl);

        function disableSpin() {
          unreachable = true;
          if (loadTimer) { clearTimeout(loadTimer); loadTimer = null; }
          if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
          iframe = null;
          loaded = false;
          media.classList.remove('is-loading');
          var badge = media.querySelector('.scmp__spin-badge');
          if (badge) badge.style.display = 'none';
          var spinner = media.querySelector('.spin-loading');
          if (spinner) spinner.style.display = 'none';
          media.removeAttribute('data-spin');
        }

        function preloadIframe() {
          if (iframe || unreachable) return;
          if (isVideo) {
            iframe = document.createElement('video');
            iframe.className = 'spin-iframe';
            iframe.src = spinUrl;
            iframe.autoplay = true;
            iframe.loop = true;
            iframe.muted = true;
            iframe.playsInline = true;
            iframe.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
            iframe.setAttribute('disablePictureInPicture', '');
            iframe.oncontextmenu = function () { return false; };
            iframe.addEventListener('error', disableSpin, { once: true });
            iframe.addEventListener('loadeddata', function () {
              loaded = true;
              if (loadTimer) { clearTimeout(loadTimer); loadTimer = null; }
              if (isMobile || media.matches(':hover')) {
                iframe.classList.add('loaded');
                media.classList.remove('is-loading');
              }
            });
          } else {
            iframe = document.createElement('iframe');
            iframe.className = 'spin-iframe';
            iframe.src = spinUrl;
            iframe.title = '360 View';
            iframe.addEventListener('error', disableSpin, { once: true });
            iframe.addEventListener('load', function () {
              loaded = true;
              if (loadTimer) { clearTimeout(loadTimer); loadTimer = null; }
              if (isMobile || media.matches(':hover')) {
                iframe.classList.add('loaded');
                media.classList.remove('is-loading');
              }
            });
          }
          loadTimer = setTimeout(function () { if (!loaded) disableSpin(); }, 8000);
          media.appendChild(iframe);
        }

        if (!isMobile) {
          media.addEventListener('mouseenter', function () {
            media.classList.add('is-spinning');
            if (iframe && loaded) {
              iframe.classList.add('loaded');
              return;
            }
            if (!iframe) preloadIframe();
            if (!loaded) media.classList.add('is-loading');
          });
          media.addEventListener('mouseleave', function () {
            media.classList.remove('is-spinning');
            media.classList.remove('is-loading');
            if (iframe) iframe.classList.remove('loaded');
          });
        }

        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                preloadIframe();
                io.unobserve(media);
              }
            });
          }, { rootMargin: '200px' });
          io.observe(media);
        }
      });
    }

    /* ---------------- initial load ----------------
       Default view: same shape (always color-compatible). The toggle only
       renders when BOTH views have stones: if other shapes are empty it
       never appears; if only other shapes exist, that view shows directly
       with no toggle. Nothing at all -> section stays hidden. */

    if (isGem) {
      gather(null, colorCompatible).then(function (pool) {
        if (!pool.length) return;
        gemPool = pool;
        if (!anchor.shape) {
          render(selectCandidates(pool, true));
          return;
        }
        var same = pool.filter(isSameShape);
        var other = pool.filter(isOtherShape);
        if (same.length) {
          viewMode = 'same';
          render(selectCandidates(same, false));
          showToggle(other.length > 0);
        } else if (other.length) {
          viewMode = 'other';
          render(selectCandidates(other, true));
          showToggle(false);
        }
      }).catch(function (err) {
        console.warn('[stone-compare] initial fetch failed:', err);
      });
    } else {
      /* Both views are fetched in parallel: the other-shapes pool decides
         whether the toggle renders, and its URLs never overlap the same-shape
         ones (they carry no shape filter), so nothing is fetched twice.
         Chaining it behind the table cost a visible ~400ms second pop.
         .catch here also keeps it handled while the same-shape leg resolves. */
      var samePool = gather(anchor.shape || null, colorCompatible);
      var otherPool = anchor.shape
        ? gather(null, otherShapeCompatible).catch(function () { return []; })
        : Promise.resolve([]);

      samePool.then(function (list) {
        if (list.length) {
          viewMode = 'same';
          render(selectCandidates(list, false));
          if (!anchor.shape) return;
          return otherPool.then(function (others) {
            showToggle(others.length > 0);
          });
        }
        if (!anchor.shape) return; /* price-window-only view found nothing: stay hidden */
        return otherPool.then(function (others) {
          if (!others.length) return; /* true price outlier: stay hidden */
          viewMode = 'other';
          render(selectCandidates(others, true));
          showToggle(false);
        });
      }).catch(function (err) {
        console.warn('[stone-compare] initial fetch failed:', err);
      });
    }
  }

  function boot() {
    document.querySelectorAll('.scmp-wrapper[data-mode]').forEach(initWrapper);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
